import { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Alert,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableContainer
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    useTable,
    tableFeatures,
    rowPaginationFeature,
    columnVisibilityFeature,
    createPaginatedRowModel,
    flexRender,
    ColumnDef
} from '@tanstack/react-table';
import dayjs from 'dayjs';

import { ContentWrapper } from '@/components/ContentWrapper';
import {
    FONT_WEIGHT,
    Heading,
    LINE_HEIGHT,
    PageSubHeading,
    SMRegular,
    SMBold,
    TextMDSemiBold
} from '@/typography/Typography';
import { ViewSwitch } from '@/components/shared/ViewSwitch';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import { DateRangeDropdown } from '@/components/shared/DateRangeDropdown';
import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { DangerButton, PrimaryButton } from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { Dropdown, DropdownType } from '@/components/shared/Dropdown';
import { TablePagination } from '@/components/shared/TablePagination';
import { Typography } from '@mui/material';
import { useUser } from '@/contexts/user/UserContext';
import { UserRoles } from '@/models/company';
import { useAdminCompanies, useAppDispatch } from '@/lib/hooks';
import { getAdminCompanies } from '@/lib/slices/paymentsSlice';
import { DATE_FORMAT } from '@/lib/constants';
import {
    ReportDefinitionResponse,
    ReportColumnSchema,
    ReportChartConfig,
    GlobalExecuteRequest,
    ReportExecutionScope
} from '@/models/reportAI';
import {
    useGlobalReportDefinitions,
    useDeleteGlobalDefinition,
    useExecuteGlobalReport,
    REPORT_AI_QUERY_KEYS
} from '@/services/reportAIQuery';
import { useQueryClient } from '@tanstack/react-query';
import { TabErrorBoundary } from '@/components/shared/TabErrorBoundary';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    Legend,
    CartesianGrid
} from 'recharts';

// ─── Types ───────────────────────────────────────────────────────────────────

interface JSONSchemaProperty {
    type?: string;
    format?: string;
    title?: string;
    enum?: string[];
}

interface JSONSchema {
    properties?: Record<string, JSONSchemaProperty>;
    required?: string[];
}

// ─── Helpers (shared with DynamicReportViewer) ───────────────────────────────

const formatCellValue = (
    value: unknown,
    schema: Pick<ReportColumnSchema, 'type'> & { currencyKey?: string },
    row?: Record<string, unknown>
): string => {
    if (value === null || value === undefined) return '—';
    switch (schema.type) {
        case 'currency': {
            const resolvedKey =
                schema.currencyKey && row
                    ? Object.keys(row).find(
                          (k) =>
                              k.toLowerCase() ===
                              schema.currencyKey!.toLowerCase()
                      )
                    : undefined;
            const code =
                resolvedKey && row ? String(row[resolvedKey] ?? 'DKK') : 'DKK';
            return typeof value === 'number'
                ? new Intl.NumberFormat('da-DK', {
                      style: 'currency',
                      currency: code
                  }).format(value)
                : String(value);
        }
        case 'date':
            return dayjs(String(value)).format(DATE_FORMAT.DAY_MONTH_YEAR);
        case 'datetime':
            return dayjs(String(value)).format(DATE_FORMAT.FULL_DATE_TIME);
        case 'boolean':
            return value ? 'Yes' : 'No';
        default:
            return String(value);
    }
};

const reportTableFeatures = tableFeatures({
    rowPaginationFeature,
    columnVisibilityFeature,
    paginatedRowModel: createPaginatedRowModel()
});

const EMPTY_COLUMN: ColumnDef<
    typeof reportTableFeatures,
    Record<string, unknown>
>[] = [{ id: '_empty', accessorKey: '_empty', header: '_empty' }];

const CHART_COLORS = [
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#14B8A6',
    '#F97316',
    '#6366F1',
    '#84CC16',
    '#06B6D4',
    '#E11D48'
];

const formatTooltipValue = (value: number) => value.toLocaleString('da-DK');

const MAX_PIE_SLICES = 8;

const aggregatePieData = (
    rows: Record<string, unknown>[],
    nameKey: string,
    valueKey: string
): Record<string, unknown>[] => {
    if (rows.length <= MAX_PIE_SLICES) return rows;
    const sorted = [...rows].sort(
        (a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0)
    );
    const top = sorted.slice(0, MAX_PIE_SLICES - 1);
    const rest = sorted.slice(MAX_PIE_SLICES - 1);
    const othersValue = rest.reduce(
        (sum, r) => sum + (Number(r[valueKey]) || 0),
        0
    );
    return [
        ...top,
        { [nameKey]: `Others (${rest.length})`, [valueKey]: othersValue }
    ];
};

const getChartHeight = (type: string, rowCount: number): number => {
    if (type === 'pie') return Math.min(420, Math.max(320, rowCount * 10));
    if (type === 'bar') return Math.min(500, Math.max(300, rowCount * 28));
    return 320;
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const MetaBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.greyBorder}`,
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap'
}));

const FilterBox = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: '8px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.greySurface,
    border: `1px solid ${theme.palette.greyBorder}`
}));

// ─── Parameter form (reused across scopes) ───────────────────────────────────

interface ParamFormProps {
    parameterJsonSchema: JSONSchema;
    startDate: Date;
    endDate: Date;
    timeFilter: string;
    extraParams: Record<string, string | undefined>;
    setTimeFilter: (v: string) => void;
    setStartDate: (d: Date) => void;
    setEndDate: (d: Date) => void;
    setExtraParams: React.Dispatch<
        React.SetStateAction<Record<string, string | undefined>>
    >;
}

const filterOptions = [
    { value: 'currentMonth', name: 'Current Month' },
    { value: 'lastMonth', name: 'Last Month' },
    { value: 'currentYear', name: 'Current Year' },
    { value: 'lastYear', name: 'Last Year' }
];

const ParamForm = ({
    parameterJsonSchema,
    startDate,
    endDate,
    timeFilter,
    extraParams,
    setTimeFilter,
    setStartDate,
    setEndDate,
    setExtraParams
}: ParamFormProps) => {
    const dateKeys = useMemo(
        () =>
            Object.entries(parameterJsonSchema.properties ?? {})
                .filter(
                    ([, p]) => p.format === 'date' || p.format === 'date-time'
                )
                .map(([k]) => k),
        [parameterJsonSchema]
    );

    const extraParamEntries = useMemo(
        () =>
            Object.entries(parameterJsonSchema.properties ?? {}).filter(
                ([, p]) => p.format !== 'date' && p.format !== 'date-time'
            ),
        [parameterJsonSchema]
    );

    const filtereDateData = (option: string) => {
        if (option === 'currentMonth') {
            setStartDate(dayjs().startOf('month').toDate());
            setEndDate(dayjs().endOf('month').toDate());
        } else if (option === 'lastMonth') {
            setStartDate(
                dayjs().subtract(1, 'month').startOf('month').toDate()
            );
            setEndDate(dayjs().subtract(1, 'month').endOf('month').toDate());
        } else if (option === 'currentYear') {
            setStartDate(dayjs().startOf('year').toDate());
            setEndDate(dayjs().endOf('year').toDate());
        } else if (option === 'lastYear') {
            setStartDate(dayjs().subtract(1, 'year').startOf('year').toDate());
            setEndDate(dayjs().subtract(1, 'year').endOf('year').toDate());
        }
    };

    const onMonthChange = (date: Date) => {
        setTimeFilter('customMonth');
        setStartDate(dayjs(date).startOf('month').toDate());
        setEndDate(dayjs(date).endOf('month').toDate());
    };

    return (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <SMBold noWrap>Filter by:</SMBold>
            {dateKeys.length > 0 && (
                <DateRangeDropdown
                    startDate={startDate}
                    endDate={endDate}
                    timeFilter={timeFilter}
                    setTimeFilter={setTimeFilter}
                    filterOptions={filterOptions}
                    filtereDateData={filtereDateData}
                    onMonthChange={onMonthChange}
                    showDatePicker={false}
                />
            )}
            {extraParamEntries.map(([key, prop]) =>
                prop.enum && prop.enum.length > 0 ? (
                    <div key={key} style={{ minWidth: '200px' }}>
                        <Dropdown
                            allowClear
                            label={prop.title ?? key}
                            choices={prop.enum.map((v) => ({
                                label: v,
                                value: v
                            }))}
                            value={extraParams[key] ?? null}
                            onChange={(v: string) =>
                                setExtraParams((prev) => ({
                                    ...prev,
                                    [key]: v
                                }))
                            }
                        />
                    </div>
                ) : (
                    <div key={key} style={{ minWidth: '180px' }}>
                        <InputText
                            placeholder={prop.title ?? key}
                            size="small"
                            inputStyles={{ backgroundColor: '#fff' }}
                            type={
                                prop.type === 'number' ||
                                prop.type === 'integer'
                                    ? 'number'
                                    : 'text'
                            }
                            value={extraParams[key] ?? ''}
                            onChange={(e) =>
                                setExtraParams((prev) => ({
                                    ...prev,
                                    [key]: e.target.value
                                }))
                            }
                        />
                    </div>
                )
            )}
        </Stack>
    );
};

// ─── Results table ────────────────────────────────────────────────────────────

interface ResultsTableProps {
    columns: string[];
    rows: Record<string, unknown>[];
    totalRows: number;
    columnSchema: ReportColumnSchema[];
    isFetching: boolean;
    isError: boolean;
    error: unknown;
    definitionName: string;
    chartConfigStr?: string;
}

const ResultsTable = ({
    rows,
    totalRows,
    columnSchema,
    isFetching,
    isError,
    error,
    definitionName,
    chartConfigStr
}: ResultsTableProps) => {
    const columnSchemaMap = useMemo(
        () =>
            Object.fromEntries(
                columnSchema
                    .filter((c) => c?.key != null)
                    .map((c) => [c.key.toLowerCase(), c])
            ),
        [columnSchema]
    );

    const tableColumns = useMemo<
        ColumnDef<typeof reportTableFeatures, Record<string, unknown>>[]
    >(() => {
        if (!rows?.length) return [];
        const rowKeys = Object.keys(rows[0]);
        return rowKeys
            .filter((rowKey) => {
                const schemaEntry = columnSchema.find(
                    (c) =>
                        c?.key != null &&
                        c.key.toLowerCase() === rowKey.toLowerCase()
                );
                return !schemaEntry?.hidden;
            })
            .map((rowKey) => {
                const schemaEntry = columnSchema.find(
                    (c) =>
                        c?.key != null &&
                        c.key.toLowerCase() === rowKey.toLowerCase()
                );
                const header: string = schemaEntry?.label ?? rowKey;
                const colDef: ColumnDef<
                    typeof reportTableFeatures,
                    Record<string, unknown>
                > = {
                    id: rowKey,
                    accessorKey: rowKey,
                    header
                };
                if (schemaEntry) {
                    colDef.cell = (info) =>
                        formatCellValue(
                            info.getValue(),
                            schemaEntry,
                            info.row.original
                        );
                }
                return colDef;
            });
    }, [columnSchema, rows]);

    const hasRows = (rows?.length ?? 0) > 0 && tableColumns.length > 0;

    const chartConfig: ReportChartConfig | null = useMemo(() => {
        if (!chartConfigStr) return null;
        try {
            return JSON.parse(chartConfigStr);
        } catch {
            return null;
        }
    }, [chartConfigStr]);

    const { chartXKey, chartYKeys } = useMemo(() => {
        const rowKeys = rows?.length ? Object.keys(rows[0]) : [];
        const resolve = (axisKey: string | undefined): string => {
            if (!axisKey) return '';
            return (
                rowKeys.find(
                    (k) => k.toLowerCase() === axisKey.toLowerCase()
                ) ?? axisKey
            );
        };
        // Support multiple data keys via dataKeys array, falling back to single yAxis
        const resolvedDataKeys: string[] = chartConfig?.dataKeys?.length
            ? chartConfig.dataKeys.map((dk) => resolve(dk))
            : chartConfig?.yAxis
              ? [resolve(chartConfig.yAxis)]
              : [];
        return {
            chartXKey: resolve(chartConfig?.xAxis),
            chartYKeys: resolvedDataKeys
        };
    }, [rows, chartConfig]);

    // Whether X-axis labels need rotation (long text or many data points)
    const xAxisAngle = useMemo(() => {
        if (!rows?.length || !chartXKey) return 0;
        const maxLen = Math.max(
            ...rows.map((r) => String(r[chartXKey] ?? '').length)
        );
        if (rows.length > 8 || maxLen > 12) return -35;
        if (rows.length > 5 || maxLen > 8) return -20;
        return 0;
    }, [rows, chartXKey]);

    const pieData = useMemo(
        () =>
            chartConfig?.type === 'pie' && rows
                ? aggregatePieData(rows, chartXKey, chartYKeys[0] ?? '')
                : (rows ?? []),
        [chartConfig?.type, rows, chartXKey, chartYKeys]
    );

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);

    const table = useTable({
        features: reportTableFeatures,
        data: rows ?? [],
        columns: tableColumns.length > 0 ? tableColumns : EMPTY_COLUMN,
        state: { pagination: { pageIndex: page, pageSize } },
        onPaginationChange: (updater) => {
            const next =
                typeof updater === 'function'
                    ? updater({ pageIndex: page, pageSize })
                    : updater;
            setPage(next.pageIndex);
            setPageSize(next.pageSize);
        }
    });

    const rowsPerPageOptions = [
        { label: '25', value: 25 },
        { label: '50', value: 50 },
        { label: '100', value: 100 }
    ];

    return (
        <TableCard>
            <CardHeader
                label={definitionName}
                chip={
                    hasRows && !isFetching ? (
                        <Chip
                            label={
                                <SMBold>
                                    {totalRows.toLocaleString()} rows
                                </SMBold>
                            }
                            color="success"
                            size="small"
                        />
                    ) : undefined
                }
            />

            {isError && !isFetching && (
                <Box px={2} pb={2}>
                    <Alert severity="error">
                        {(error as Error)?.message ??
                            'Failed to execute report.'}
                    </Alert>
                </Box>
            )}

            {isFetching && (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={32} />
                </Box>
            )}

            {hasRows && !isFetching && (
                <>
                    <TableContainer>
                        <Table size="small">
                            <TableHeader>
                                {table.getHeaderGroups().map((hg) => (
                                    <TableRow key={hg.id}>
                                        {hg.headers.map((header) => {
                                            const col =
                                                columnSchemaMap[
                                                    header.id.toLowerCase()
                                                ];
                                            return (
                                                <TableHeaderCell
                                                    key={header.id}
                                                    align={col?.align ?? 'left'}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                </TableHeaderCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => {
                                            const col =
                                                columnSchemaMap[
                                                    cell.column.id.toLowerCase()
                                                ];
                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    align={col?.align ?? 'left'}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef
                                                            .cell,
                                                        cell.getContext()
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        count={totalRows}
                        page={page}
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={rowsPerPageOptions}
                        onRowsPerPageChange={(value) => {
                            if (typeof value === 'number') {
                                setPageSize(value);
                                setPage(0);
                            }
                        }}
                        memoryMode
                        onPageChange={(p) => {
                            if (typeof p === 'number') setPage(p);
                        }}
                    />

                    {/* Chart */}
                    {chartConfig &&
                        chartConfig.type !== 'text' &&
                        chartYKeys.length > 0 && (
                            <Box px={2} pt={3} pb={2}>
                                <TextMDSemiBold sx={{ mb: 1 }}>
                                    {chartConfig.title ?? 'Chart'}
                                </TextMDSemiBold>
                                <Box
                                    height={getChartHeight(
                                        chartConfig.type,
                                        rows.length
                                    )}
                                >
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        {chartConfig.type === 'pie' ? (
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    dataKey={chartYKeys[0]}
                                                    nameKey={chartXKey}
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius="80%"
                                                    innerRadius="45%"
                                                    paddingAngle={2}
                                                    label={({
                                                        name,
                                                        percent
                                                    }) =>
                                                        percent > 0.04
                                                            ? `${name} (${(
                                                                  percent * 100
                                                              ).toFixed(0)}%)`
                                                            : ''
                                                    }
                                                    labelLine={false}
                                                >
                                                    {pieData.map((_, idx) => (
                                                        <Cell
                                                            key={idx}
                                                            fill={
                                                                CHART_COLORS[
                                                                    idx %
                                                                        CHART_COLORS.length
                                                                ]
                                                            }
                                                        />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    formatter={
                                                        formatTooltipValue
                                                    }
                                                />
                                                <Legend
                                                    layout="horizontal"
                                                    verticalAlign="bottom"
                                                    align="center"
                                                    wrapperStyle={{
                                                        fontSize: 12,
                                                        paddingTop: 12
                                                    }}
                                                />
                                            </PieChart>
                                        ) : chartConfig.type === 'line' ? (
                                            <LineChart data={rows}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey={chartXKey}
                                                    angle={xAxisAngle}
                                                    textAnchor={
                                                        xAxisAngle
                                                            ? 'end'
                                                            : 'middle'
                                                    }
                                                    height={
                                                        xAxisAngle ? 80 : 30
                                                    }
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <Tooltip
                                                    formatter={
                                                        formatTooltipValue
                                                    }
                                                />
                                                {chartYKeys.length > 1 && (
                                                    <Legend
                                                        wrapperStyle={{
                                                            fontSize: 12,
                                                            paddingTop: 8
                                                        }}
                                                    />
                                                )}
                                                {chartYKeys.map((dk, idx) => (
                                                    <Line
                                                        key={dk}
                                                        type="monotone"
                                                        dataKey={dk}
                                                        stroke={
                                                            CHART_COLORS[
                                                                idx %
                                                                    CHART_COLORS.length
                                                            ]
                                                        }
                                                        strokeWidth={2}
                                                        dot={{ r: 3 }}
                                                    />
                                                ))}
                                            </LineChart>
                                        ) : (
                                            <BarChart data={rows}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    dataKey={chartXKey}
                                                    angle={xAxisAngle}
                                                    textAnchor={
                                                        xAxisAngle
                                                            ? 'end'
                                                            : 'middle'
                                                    }
                                                    height={
                                                        xAxisAngle ? 80 : 30
                                                    }
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 12 }}
                                                />
                                                <Tooltip
                                                    formatter={
                                                        formatTooltipValue
                                                    }
                                                />
                                                {chartYKeys.length > 1 && (
                                                    <Legend
                                                        wrapperStyle={{
                                                            fontSize: 12,
                                                            paddingTop: 8
                                                        }}
                                                    />
                                                )}
                                                {chartYKeys.map((dk, idx) => (
                                                    <Bar
                                                        key={dk}
                                                        dataKey={dk}
                                                        fill={
                                                            CHART_COLORS[
                                                                idx %
                                                                    CHART_COLORS.length
                                                            ]
                                                        }
                                                    />
                                                ))}
                                            </BarChart>
                                        )}
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        )}
                </>
            )}
        </TableCard>
    );
};

// ─── GlobalReportViewer ───────────────────────────────────────────────────────

interface GlobalReportViewerProps {
    definition: ReportDefinitionResponse;
    onDelete?: () => void;
}

export const GlobalReportViewer = ({
    definition,
    onDelete
}: GlobalReportViewerProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { companies } = useAdminCompanies();

    const scope: ReportExecutionScope =
        definition.executionScope ?? 'SingleCompany';

    const columnSchema: ReportColumnSchema[] = useMemo(() => {
        try {
            return JSON.parse(definition.columnSchema);
        } catch {
            return [];
        }
    }, [definition.columnSchema]);

    const parameterJsonSchema: JSONSchema = useMemo(() => {
        try {
            return JSON.parse(definition.parameterSchema);
        } catch {
            return { properties: {} };
        }
    }, [definition.parameterSchema]);

    const dateKeys = useMemo(
        () =>
            Object.entries(parameterJsonSchema.properties ?? {})
                .filter(
                    ([, p]) => p.format === 'date' || p.format === 'date-time'
                )
                .map(([k]) => k),
        [parameterJsonSchema]
    );

    const [startDate, setStartDate] = useState<Date>(
        dayjs().startOf('month').toDate()
    );
    const [endDate, setEndDate] = useState<Date>(
        dayjs().endOf('month').toDate()
    );
    const [timeFilter, setTimeFilter] = useState('currentMonth');
    const [extraParams, setExtraParams] = useState<
        Record<string, string | undefined>
    >({});

    // For SingleCompany scope
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    // For MultiCompany scope
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);

    const [runEnabled, setRunEnabled] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        dispatch(getAdminCompanies());
    }, [dispatch]);

    const params = useMemo<Record<string, string | undefined>>(() => {
        const built: Record<string, string | undefined> = { ...extraParams };
        if (dateKeys[0])
            built[dateKeys[0]] = dayjs(startDate).format('YYYY-MM-DD');
        if (dateKeys[1])
            built[dateKeys[1]] = dayjs(endDate).format('YYYY-MM-DD');
        return built;
    }, [startDate, endDate, extraParams, dateKeys]);

    // All scopes use the global endpoint (IsForGlobalAdmin reports are excluded from the per-company endpoint)
    const globalBody: GlobalExecuteRequest = useMemo(
        () => ({
            companyIds:
                scope === 'AllCompanies'
                    ? []
                    : scope === 'SingleCompany'
                      ? selectedCompanyId
                          ? [selectedCompanyId]
                          : []
                      : selectedCompanyIds,
            parameters: params
        }),
        [scope, selectedCompanyId, selectedCompanyIds, params]
    );

    const {
        data: globalData,
        isFetching: globalFetching,
        isError: globalError,
        error: globalErrorObj
    } = useExecuteGlobalReport(
        definition.reportId,
        globalBody,
        runEnabled &&
            (scope === 'AllCompanies' ||
                (scope === 'SingleCompany' && !!selectedCompanyId) ||
                (scope === 'MultiCompany' && selectedCompanyIds.length > 0))
    );

    const companyChoices = useMemo(
        () =>
            (companies ?? [])
                .map((c) => ({ label: c.name, value: c.companyId }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [companies]
    );

    const handleRun = () => setRunEnabled(true);

    const isFetching = globalFetching;
    const isError = globalError;
    const errorObj = globalErrorObj;

    const resultRows = globalData?.rows ?? [];
    const resultColumns = globalData?.columns ?? [];
    const totalRows = globalData?.totalRows ?? 0;

    return (
        <Box>
            <FilterBox>
                {/* Top row: Company + Filters inline */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                >
                    {/* Company selector */}
                    {scope === 'SingleCompany' && (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <SMBold noWrap>
                                {t('global_reports.company')}
                            </SMBold>
                            <Box minWidth={220}>
                                <Dropdown
                                    label={t('global_reports.select_company')}
                                    choices={companyChoices}
                                    value={selectedCompanyId || null}
                                    onChange={(v: string) => {
                                        setSelectedCompanyId(v);
                                        setRunEnabled(false);
                                    }}
                                />
                            </Box>
                        </Stack>
                    )}

                    {scope === 'MultiCompany' && (
                        <Stack direction="row" alignItems="center" gap={1}>
                            <SMBold noWrap>
                                {t('global_reports.select_companies')}
                            </SMBold>
                            <Box minWidth={320}>
                                <Dropdown
                                    type={DropdownType.Multi}
                                    label={t('global_reports.select_companies')}
                                    choices={companyChoices}
                                    value={selectedCompanyIds}
                                    onChange={(ids: string[]) => {
                                        setSelectedCompanyIds(ids ?? []);
                                        setRunEnabled(false);
                                    }}
                                />
                            </Box>
                        </Stack>
                    )}

                    {scope === 'AllCompanies' && (
                        <SMRegular color="text.secondary">
                            {t('global_reports.all_companies_hint')}
                        </SMRegular>
                    )}

                    {/* Separator */}
                    {scope === 'SingleCompany' && (
                        <Box
                            sx={{
                                width: '1px',
                                height: 32,
                                backgroundColor: 'greyBorder',
                                mx: 0.5
                            }}
                        />
                    )}

                    {/* Parameter filters inline */}
                    <ParamForm
                        parameterJsonSchema={parameterJsonSchema}
                        startDate={startDate}
                        endDate={endDate}
                        timeFilter={timeFilter}
                        extraParams={extraParams}
                        setTimeFilter={setTimeFilter}
                        setStartDate={(d) => {
                            setStartDate(d);
                            setRunEnabled(false);
                        }}
                        setEndDate={(d) => {
                            setEndDate(d);
                            setRunEnabled(false);
                        }}
                        setExtraParams={(fn) => {
                            setExtraParams(fn);
                            setRunEnabled(false);
                        }}
                    />
                </Stack>

                {/* Bottom row: Run + Delete */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    pt={0.5}
                >
                    <PrimaryButton
                        size="small"
                        onClick={handleRun}
                        disabled={
                            isFetching ||
                            (scope === 'SingleCompany' && !selectedCompanyId) ||
                            (scope === 'MultiCompany' &&
                                selectedCompanyIds.length === 0)
                        }
                    >
                        {t('global_reports.run')}
                    </PrimaryButton>

                    {onDelete && (
                        <Stack direction="row" alignItems="center" gap={1}>
                            {confirmDelete ? (
                                <>
                                    <SMRegular color="error.main">
                                        {t('global_reports.confirm_delete')}
                                    </SMRegular>
                                    <DangerButton
                                        size="small"
                                        onClick={() => {
                                            setConfirmDelete(false);
                                            onDelete();
                                        }}
                                    >
                                        {t('global_reports.confirm')}
                                    </DangerButton>
                                    <PrimaryButton
                                        size="small"
                                        onClick={() => setConfirmDelete(false)}
                                    >
                                        {t('global_reports.cancel')}
                                    </PrimaryButton>
                                </>
                            ) : (
                                <DangerButton
                                    size="small"
                                    onClick={() => setConfirmDelete(true)}
                                >
                                    {t('global_reports.delete_report')}
                                </DangerButton>
                            )}
                        </Stack>
                    )}
                </Stack>
            </FilterBox>

            {/* Failed companies warning */}
            {globalData &&
                globalData.failedCompanyIds.length > 0 &&
                !globalFetching && (
                    <Box px={2} py={1}>
                        <Alert severity="warning">
                            {t('global_reports.failed_companies', {
                                count: globalData.failedCompanyIds.length,
                                ids: globalData.failedCompanyIds.join(', ')
                            })}
                        </Alert>
                    </Box>
                )}

            {/* Results table */}
            <ResultsTable
                columns={resultColumns}
                rows={resultRows}
                totalRows={totalRows}
                columnSchema={columnSchema}
                isFetching={isFetching}
                isError={isError}
                error={errorObj}
                definitionName={definition.name}
                chartConfigStr={definition.chartConfig}
            />

            {/* Global metadata */}
            {globalData && !globalFetching && scope !== 'SingleCompany' && (
                <MetaBox>
                    <SMRegular color="text.secondary">
                        {t('global_reports.companies_executed', {
                            count: globalData.totalCompaniesExecuted
                        })}
                    </SMRegular>
                </MetaBox>
            )}
        </Box>
    );
};

// ─── GlobalReportsDashboard ───────────────────────────────────────────────────

export const GlobalReportsDashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useUser();

    const isSuperAdmin = user?.role === UserRoles.SuperAdmin;

    useEffect(() => {
        if (!isSuperAdmin) {
            navigate('/', { replace: true });
        }
    }, [isSuperAdmin, navigate]);

    const { data: definitions, isPending } =
        useGlobalReportDefinitions(isSuperAdmin);
    const { mutate: deleteDefinition } = useDeleteGlobalDefinition();

    const [tabIndex, setTabIndex] = useState<number | null>(null);

    const viewSwitchItems = useMemo(
        () =>
            (definitions ?? []).map((def) => ({
                primaryText: def.name,
                route: `global_report_${def.reportId}`,
                badgeText: '',
                routeComponent: (
                    <TabErrorBoundary key={def.reportId} tabName={def.name}>
                        <GlobalReportViewer
                            definition={def}
                            onDelete={
                                isSuperAdmin
                                    ? () => {
                                          navigate('..', {
                                              relative: 'path',
                                              replace: true
                                          });
                                          deleteDefinition(def.reportId, {
                                              onSuccess: () =>
                                                  queryClient.invalidateQueries(
                                                      {
                                                          queryKey: [
                                                              REPORT_AI_QUERY_KEYS.GLOBAL_DEFINITIONS
                                                          ]
                                                      }
                                                  )
                                          });
                                      }
                                    : undefined
                            }
                        />
                    </TabErrorBoundary>
                )
            })),
        [definitions, isSuperAdmin, deleteDefinition, navigate, queryClient]
    );

    if (!isSuperAdmin) return null;

    return (
        <ContentWrapper>
            <>
                <Box m={1} display="flex" justifyContent="space-between">
                    <Typography variant="h4" marginBottom="5px">
                        <Heading
                            fontSize={20}
                            fontWeight={FONT_WEIGHT.MEDIUM}
                            lineHeight={LINE_HEIGHT.XLARGE}
                        >
                            {t('global_reports.title')}
                        </Heading>
                        <PageSubHeading>
                            {t('global_reports.subtitle')}
                        </PageSubHeading>
                    </Typography>
                </Box>

                {isPending && (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {!isPending && viewSwitchItems.length === 0 && (
                    <Box px={2} py={4}>
                        <SMRegular color="text.secondary">
                            {t('global_reports.no_reports')}
                        </SMRegular>
                    </Box>
                )}

                {!isPending && viewSwitchItems.length > 0 && (
                    <ViewSwitch
                        itemList={viewSwitchItems}
                        setSelectedIndex={setTabIndex}
                        selectedIndex={tabIndex}
                    />
                )}
            </>
        </ContentWrapper>
    );
};
